import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check, Sparkles, Shield, Lock, Mail, Globe, PenTool, FileText, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Layout } from '@/components/wiki/Layout';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useSEO } from '@/hooks/useSEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showWriterPlan = searchParams.get('plan') === 'writer';
  const { user, getAuthHeader, isSuperAdmin } = useAuth();
  const [pricing, setPricing] = useState({ monthly_price: 69.00, yearly_price: 599.00, writer_price: 2400.00 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // SEO meta tags
  useSEO({
    title: 'Pricing - Claim Your Page',
    description: 'Claim your company or executive page on Handshake. Add official contact information, social links, and lock your page from edits. Plans starting at $69/month.',
  });

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/pricing`);
        setPricing(response.data);
      } catch (error) {
        console.error('Error fetching pricing:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, []);

  const handleCheckout = async (planType) => {
    if (!user) {
      toast.error('Please log in to subscribe');
      navigate('/login');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        page_id: 'premium-subscription',
        plan_type: planType,
        origin_url: window.location.origin
      }, {
        headers: getAuthHeader()
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Failed to start checkout');
      console.error('Checkout error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const monthlyEquivalent = (pricing.yearly_price / 12).toFixed(2);
  const yearlySavings = ((pricing.monthly_price * 12) - pricing.yearly_price).toFixed(0);
  const savingsPercent = Math.round((1 - pricing.yearly_price / (pricing.monthly_price * 12)) * 100);

  const claimFeatures = [
    { icon: Shield, text: 'Verified "Sponsored" badge on your page' },
    { icon: Lock, text: 'Lock your page to prevent unauthorized edits' },
    { icon: Globe, text: 'Add official website and social media links' },
    { icon: Mail, text: 'Display verified contact information' },
  ];

  const writerFeatures = [
    { icon: PenTool, text: 'Professional writer creates your page content' },
    { icon: FileText, text: 'Listed first in the relevant category' },
    { icon: Users, text: 'Sourcing call and citations research included' },
    { icon: Shield, text: 'All Claim features included (badge, lock, links)' },
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-12" data-testid="pricing-page">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-[#36c] hover:text-[#2a4b8d] hover:bg-[#eaecf0] mb-4" data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>

        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#eaf3ff] rounded-full text-[#36c] text-sm font-medium mb-6">
            <Sparkles className="h-4 w-4" />
            Premium Features
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#202122] mb-4">
            Claim Your Page
          </h1>
          <p className="text-[#54595d] text-base max-w-2xl mx-auto">
            Take control of your business profile. Add verified contact information, social links, and protect your page from unauthorized changes.
          </p>
        </div>

        {/* Super Admin Notice */}
        {isSuperAdmin() && (
          <div className="max-w-3xl mx-auto mb-8 bg-[#eaf3ff] border border-[#36c]/30 rounded-sm p-4">
            <p className="text-[#36c] text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <strong>Super Admin:</strong> You can add premium features to any page without payment from the Edit page.
            </p>
          </div>
        )}

        {/* Pricing Cards */}
        {loading ? (
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            <div className="bg-white rounded-sm p-8 border border-[#c8ccd1] animate-pulse h-80" />
            <div className="bg-white rounded-sm p-8 border border-[#c8ccd1] animate-pulse h-80" />
            <div className="bg-white rounded-sm p-8 border border-[#c8ccd1] animate-pulse h-80" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-8">
            {/* Monthly Plan */}
            <div className="bg-white rounded-sm p-8 border border-[#c8ccd1] hover:border-[#36c] transition-colors">
              <div className="mb-6">
                <h3 className="text-lg font-medium text-[#54595d] mb-2">Monthly</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-[#202122]">${pricing.monthly_price}</span>
                  <span className="text-[#54595d]">/month</span>
                </div>
                <p className="text-sm text-[#72777d] mt-2">Billed monthly, cancel anytime</p>
              </div>
              <Button 
                onClick={() => handleCheckout('monthly')}
                disabled={processing}
                className="w-full bg-[#f8f9fa] hover:bg-[#eaecf0] text-[#202122] border border-[#a2a9b1] font-medium py-6"
                data-testid="monthly-checkout-btn"
              >
                {processing ? 'Processing...' : 'Get Started'}
              </Button>
            </div>

            {/* Yearly Plan */}
            <div className="bg-white rounded-sm p-8 border-2 border-[#36c] relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-[#36c] text-white text-xs font-bold px-3 py-1 rounded-full">
                  SAVE {savingsPercent}%
                </span>
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-medium text-[#36c] mb-2">Yearly</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-[#202122]">${pricing.yearly_price}</span>
                  <span className="text-[#54595d]">/year</span>
                </div>
                <p className="text-sm text-[#72777d] mt-2">
                  ${monthlyEquivalent}/month · Cancel anytime
                </p>
              </div>
              <Button 
                onClick={() => handleCheckout('yearly')}
                disabled={processing}
                className="w-full bg-[#36c] hover:bg-[#2a4b8d] text-white font-medium py-6"
                data-testid="yearly-checkout-btn"
              >
                {processing ? 'Processing...' : 'Get Started'}
              </Button>
            </div>

            {/* Hire a Writer Plan */}
            <div className={`bg-white rounded-sm p-8 border ${showWriterPlan ? 'border-2 border-[#36c]' : 'border-[#c8ccd1]'} hover:border-[#36c] transition-colors relative`}>
              {showWriterPlan && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-[#36c] text-white text-xs font-bold px-3 py-1 rounded-full">
                    RECOMMENDED
                  </span>
                </div>
              )}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-[#54595d] mb-2 flex items-center gap-2">
                  <PenTool className="h-4 w-4 text-[#36c]" />
                  Hire a Writer
                </h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-[#202122]">$3,600</span>
                </div>
                <p className="text-sm text-[#72777d] mt-2">One-time · Lifetime Claim benefits</p>
              </div>
              <Button 
                onClick={() => handleCheckout('writer')}
                disabled={processing}
                className="w-full bg-[#36c] hover:bg-[#2a4b8d] text-white font-medium py-6"
                data-testid="writer-checkout-btn"
              >
                {processing ? 'Processing...' : 'Get Started'}
              </Button>
            </div>
          </div>
        )}

        {/* Auto-renewal notice */}
        <div className="max-w-3xl mx-auto mb-12 text-center">
          <p className="text-[#72777d] text-sm">
            Monthly and yearly subscriptions auto-renew until cancelled. You can cancel anytime from your{' '}
            <Link to="/profile" className="text-[#36c] hover:underline">Profile</Link>.
            The "Hire a Writer" option is a one-time payment with lifetime premium benefits.
          </p>
        </div>

        {/* Features */}
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Claim Features */}
            <div>
              <h2 className="text-xl font-semibold text-[#202122] mb-6 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-[#36c]" />
                Claim Features
              </h2>
              <div className="space-y-3">
                {claimFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-[#f8f9fa] rounded-sm border border-[#c8ccd1]">
                    <div className="w-8 h-8 rounded-full bg-[#eaf3ff] flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-4 w-4 text-[#36c]" />
                    </div>
                    <span className="text-[#202122] text-sm">{feature.text}</span>
                    <Check className="h-4 w-4 text-[#14866d] ml-auto" />
                  </div>
                ))}
              </div>
            </div>

            {/* Writer Features */}
            <div>
              <h2 className="text-xl font-semibold text-[#202122] mb-6 flex items-center gap-2">
                <PenTool className="h-5 w-5 text-[#36c]" />
                Hire a Writer Includes
              </h2>
              <div className="space-y-3">
                {writerFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-[#f8f9fa] rounded-sm border border-[#c8ccd1]">
                    <div className="w-8 h-8 rounded-full bg-[#eaf3ff] flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-4 w-4 text-[#36c]" />
                    </div>
                    <span className="text-[#202122] text-sm">{feature.text}</span>
                    <Check className="h-4 w-4 text-[#14866d] ml-auto" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mt-16">
          <h2 className="text-xl font-semibold text-[#202122] text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div className="bg-[#f8f9fa] rounded-sm p-6 border border-[#c8ccd1]">
              <h3 className="font-medium text-[#202122] mb-2">How do I claim an existing page?</h3>
              <p className="text-[#54595d] text-sm">
                After subscribing, you can claim any page you own or represent. Navigate to the page and use the "Claim" option to link it to your account.
              </p>
            </div>
            <div className="bg-[#f8f9fa] rounded-sm p-6 border border-[#c8ccd1]">
              <h3 className="font-medium text-[#202122] mb-2">Can I create new pages with premium features?</h3>
              <p className="text-[#54595d] text-sm">
                Yes! Premium subscribers can add social links, contact info, and lock pages when creating new content.
              </p>
            </div>
            <div className="bg-[#f8f9fa] rounded-sm p-6 border border-[#c8ccd1]">
              <h3 className="font-medium text-[#202122] mb-2">What happens if I cancel?</h3>
              <p className="text-[#54595d] text-sm">
                Your page will remain visible but premium features like locking and verified contact info will be removed at the end of your billing period.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        {!user && (
          <div className="text-center mt-16">
            <p className="text-[#54595d] mb-4">Already have an account?</p>
            <Link to="/login">
              <Button variant="outline" className="border-[#a2a9b1] text-[#36c] hover:bg-[#eaecf0]">
                Login
              </Button>
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}
